import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, Loader2, Server } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const TIERS = [1, 2, 3, 4, 5];

export const TierCard = () => {
  const { t, locale } = useI18n();
  const [s, setS] = useState(null);
  const [tier, setTier] = useState(null);
  const [active, setActive] = useState("");
  const [waiting, setWaiting] = useState("");
  const [busy, setBusy] = useState(false);
  const n = (x) => new Intl.NumberFormat(locale).format(x);

  const load = (data) => {
    setS(data);
    setTier(data.tier ?? null);
    setActive(String(data.tier_limit));
    setWaiting(String(data.waiting));
  };
  useEffect(() => {
    api.get("/admin/queue-tier").then(({ data }) => load(data)).catch(() => {});
  }, []);

  const pick = (v) => {
    setTier(v);
    if (v !== "custom") {
      setActive(String(s.presets[v]));
      setWaiting(String(Math.max(1000, s.presets[v] * 35)));
    }
  };
  const save = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/queue-tier", body);
      load(data);
      window.dispatchEvent(new Event("fmo-tier"));
      toast.success(t("tier.saved", { n: n(data.tier_limit) }));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  if (!s) return null;
  const d = s.detected;
  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="tier-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
        <Server className="h-5 w-5 text-[var(--gold)]" /> {t("tier.title")}
      </h2>
      <p className="mt-2 text-sm text-neutral-400">{t("tier.hint")}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-[rgba(245,197,66,0.25)] bg-black/40 p-3 text-sm" data-testid="tier-detected">
        <Cpu className="h-4 w-4 shrink-0 text-[var(--gold)]" />
        <span className="min-w-[12rem] flex-1 text-neutral-200">{t("tier.detected", { spec: `\u2068${n(d.vcpu)} vCPU / ${n(d.ram_gb)} ${t("tier.ramUnit")}\u2069`, tier: d.advised, active: `\u2068${n(d.advised_active)}\u2069` })}</span>
        {s.tier !== d.advised && (
          <button type="button" className="btn-gold btn-sm" disabled={busy} onClick={() => save({ tier: d.advised })} data-testid="tier-apply-advised">
            {t("tier.applyAdvised", { tier: d.advised })}
          </button>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label={t("tier.title")}>
        {[...TIERS, "custom"].map((v) => (
          <button key={v} type="button" role="radio" aria-checked={tier === v} onClick={() => pick(v)}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${tier === v ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-[rgba(245,197,66,0.35)] text-neutral-200 hover:border-[var(--gold)]"}`}
            data-testid={`tier-option-${v}`}>
            {v === "custom" ? t("tier.custom") : t("tier.preset", { tier: v, n: n(s.presets[v]) })}
          </button>
        ))}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="min-w-0 text-xs text-neutral-400">{t("tier.active")}
          <Input inputMode="numeric" value={active} disabled={tier !== "custom"} onChange={(e) => setActive(e.target.value.replace(/\D/g, ""))} className="mt-1" data-testid="tier-active-input" />
        </label>
        <label className="min-w-0 text-xs text-neutral-400">{t("tier.waiting")}
          <Input inputMode="numeric" value={waiting} onChange={(e) => setWaiting(e.target.value.replace(/\D/g, ""))} className="mt-1" data-testid="tier-waiting-input" />
          {tier != null && <span className="mt-1 block text-[0.7rem] text-neutral-500">{t("tier.waitingDefault", { n: n(Math.max(1000, Number(active || 0) * 35)) })}</span>}
        </label>
      </div>
      <p className="mt-3 text-xs text-neutral-500">{s.tier == null ? t("tier.envDefault", { a: n(s.env_defaults.active), w: n(s.env_defaults.waiting) }) : t("tier.liveNote")}</p>
      <button type="button" className="btn-gold btn-sm mt-3" disabled={busy || !tier || !Number(active)}
        onClick={() => save({ tier, ...(tier === "custom" ? { active: Number(active) } : {}), waiting: Number(waiting) })} data-testid="tier-save">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("tier.save")}
      </button>
    </section>
  );
};
